// Call this to register our module to main application
const moduleName = 'storefront.checkout';
const { storefrontAppDependencies } = window;

if (angular.isArray(storefrontAppDependencies)) {
    storefrontAppDependencies.push(moduleName);
}

let RS_CHECKOUT_STEP = null;

// Feature flag: require email on billing address. Postponed at client request.
// To enable: flip to true here AND in common-components/address.tpl.html.liquid
const RS_BILLING_EMAIL_REQUIRED = false;

angular.module(moduleName, [])

    // hack for history.replaceState()
    .config(function () {
        const step = location.search.slice(1)
            .split('&')
            .map((item) => {
                const [param, value] = item.split('=');
                return { param, value };
            })
            .find((item) => item.param === 'step');

        if (step) {
            RS_CHECKOUT_STEP = (step.value || '').replace(/\W/g, '') || null;
            history.replaceState(null, null, '/cart');
        }
    })

    .controller('checkoutController', [
        '$rootScope',
        '$scope',
        '$window',
        '$timeout',
        '$sce',
        '$translate',
        '$q',
        'cartService',
        'catalogService',
        'dialogService',
        'productListService',
        'rsAddressCompareService',
        'googleAnalyticsService',
        'focusService',
        'busyService',
        function (
            $rootScope,
            $scope,
            $window,
            $timeout,
            $sce,
            $translate,
            $q,
            cartService,
            catalogService,
            dialogService,
            productListService,
            rsAddressCompareService,
            googleAnalyticsService,
            focusService,
            busyService,
        ) {
            // BrowseOnly mode: the cart stays viewable, every step towards an order is disabled
            $scope.browseOnlyMode = $window.browseOnlyMode === true;

            $scope.currentStep = 'cart';
            $scope.confirmedStep = 'cart';
            $scope.applePayCode = 'ApplePay';

            // WCAG 2.1.1 (#978): the step markers used to be <span ng-click> kept
            // unreachable by CSS alone (pointer-events: none) -- a guard that never
            // applied to keyboard activation. The markers are focusable buttons now,
            // so reachability lives here and drives their ng-click, aria-disabled
            // and disabled class off the same rule: a step is reachable up to and
            // including the furthest step the customer has confirmed.
            const checkoutSteps = ['cart', 'shipping', 'payment'];

            $scope.isStepReachable = function (step) {
                return checkoutSteps.indexOf(step) <= checkoutSteps.indexOf($scope.confirmedStep);
            };

            $scope.goToStep = function (step) {
                if ($scope.isStepReachable(step)) {
                    $scope.currentStep = step;
                }
            };

            // WCAG 2.4.3: the checkout step swap (cart -> shipping -> payment) replaces
            // the visible section without moving focus, stranding keyboard users on a
            // now-hidden control. Move focus to the new step's heading on every change
            // (covers submitCart/goToPayment and the inline step-indicator clicks).
            const stepHeadingIds = {
                cart: 'step-cart-heading',
                shipping: 'step-shipping-heading',
                payment: 'step-payment-heading',
            };
            $scope.$watch('currentStep', function (newStep, oldStep) {
                if (newStep === oldStep) {
                    return;
                }
                focusService.focusRegion(stepHeadingIds[newStep]);
            });

            // WCAG 3.3.1: the "address is invalid" message renders in a column
            // beside the section heading and nothing moved focus to it, so a
            // customer using a keyboard or a screen reader had no way to learn the
            // address had been refused — the button simply stopped working. Focus
            // it whenever it appears, EXCEPT while the address-suggestion dialog
            // is up: that dialog owns focus, and the message is the fallback for
            // when it is dismissed without a choice.
            const addressErrorIds = {
                shipping: 'shipping-address-error',
                billing: 'billing-address-error',
            };
            $scope.loadedAvailPaymentMethods = false;
            $scope.existAvailShippingMethods = false;
            $scope.loadedCountryRegions = false;
            $scope.updatingShipmentAddr = false;
            $scope.updatingBillingAddr = false;
            $scope.showApplePayButton = false;

            $scope.checkout = {
                wizard: {},
                paymentMethod: {},
                shipment: {},
                payment: {},
                coupons: [],
                availCountries: [],
                loading: false,
                components: [],
                validationContainer: {},
            };

            $scope.checkout.validationContainer.addComponent = function (component) {
                $scope.checkout.components.push(component);
            };
            $scope.checkout.validationContainer.removeComponent = function (component) {
                $scope.checkout.components = _.without($scope.checkout.components, component);
            };

            $scope.cartItemsHasErrors = function () {
                if (!$scope.cart) {
                    return false;
                }

                return _.some($scope.cart.items, function (item) {
                    return !item.isValid;
                });
            };

            function validateComponents() {
                const results = $scope.checkout.components
                    .map((component) => !angular.isFunction(component.validate) || component.validate());

                return results.every(Boolean);
            }

            $scope.validateCoupon = _.throttle(function (coupon) {
                coupon.processing = true;

                return wrapLoading(function () {
                    return cartService.validateCoupon(coupon)
                        .then(function (result) {
                            return angular.extend(coupon, result.data);
                        })
                        .finally(function () {
                            coupon.processing = false;
                        });
                }, 'checkout.busy.coupon');
            }, 1000);

            $scope.applyCoupon = function (coupon) {
                coupon.processing = true;

                return wrapLoading(function () {
                    return cartService.addCoupon(coupon.code)
                        .then(function () {
                            $scope.reloadCart();
                            $rootScope.$broadcast('cartItemsChanged');
                        })
                        .finally(function () {
                            coupon.processing = false;
                        });
                }, 'checkout.busy.coupon');
            };

            $scope.removeCoupon = _.throttle(function (coupon) {
                coupon.processing = true;

                return wrapLoading(function () {
                    return cartService.removeCoupon(coupon.code)
                        .then(function () {
                            $scope.reloadCart();
                            $rootScope.$broadcast('cartItemsChanged');
                        })
                        .finally(function () {
                            coupon.processing = false;
                        });
                }, 'checkout.busy.coupon');
            }, 1000);

            $scope.selectPaymentMethod = function (paymentMethod) {
                angular.extend($scope.checkout.payment, paymentMethod);
                setPaymentAmount(paymentMethod);
            };

            function setPaymentAmount(paymentMethod) {
                if (paymentMethod == null) {
                    return;
                }

                $scope.checkout.payment.paymentGatewayCode = paymentMethod.code;
                $scope.checkout.payment.amount = angular.copy($scope.cart.total);

                if (paymentMethod.totalWithTax && angular.isDefined(paymentMethod.totalWithTax.amount)) {
                    $scope.checkout.payment.amount.amount += paymentMethod.totalWithTax.amount;
                }
            }

            $scope.goToPayment = function () {
                if ($scope.browseOnlyMode || $scope.checkout.invalidDeliveryAddress) {
                    return;
                }

                $scope.getAvailShippingMethods($scope.checkout.shipment)
                    .then(function (shippingMethods) {
                        if (shippingMethods) {
                            const method = shippingMethods
                                .find((item) => item.optionName === $scope.checkout.shipment.shipmentMethodOption);

                            if (!angular.isDefined(method)) {
                                $window.location = '/cart';
                                return;
                            }
                        }

                        const isValid = validateComponents();

                        if (isValid) {
                            $scope.updateShipment($scope.checkout.shipment, true)
                                .then(function () {
                                    $scope.currentStep = 'payment';
                                    $scope.confirmedStep = 'payment';
                                })
                                .finally(() => scroll(0, 0));
                        }
                    });
            };

            $scope.getCountryRegions = function (country) {
                return cartService.getCountryRegions(country.code3)
                    .then(function (response) {
                        return response.data;
                    })
                    .finally(function () {
                        $scope.loadedCountryRegions = true;
                    });
            };

            $scope.getAvailShippingMethods = function (shipment) {
                return wrapLoading(function () {
                    return cartService.getAvailableShippingMethods(shipment.id)
                        .then(function ({ data = [] }) {
                            $scope.existAvailShippingMethods = !!data.length;

                            return data;
                        });
                }, 'checkout.busy.shipping_options');
            };

            $scope.getAvailPaymentMethods = function () {
                return wrapLoading(function () {
                    return cartService.getAvailablePaymentMethods()
                        .then(function (response) {
                            const methods = response.data;

                            return checkApplePayAvailable(methods)
                                .then(function (applePayAvailable) {
                                    if (!applePayAvailable) {
                                        return _.filter(methods, function (item) {
                                            return item.code !== $scope.applePayCode;
                                        });
                                    }

                                    return methods;
                                })
                                .finally(function () {
                                    $scope.loadedAvailPaymentMethods = true;
                                });
                        });
                }, 'checkout.busy.payment_options');
            };

            function checkApplePayAvailable(methods) {
                if ($window.ApplePaySession) {
                    const applePayMethod = _.findWhere(methods, { code: $scope.applePayCode });

                    if (applePayMethod) {
                        const merchantIdSeting = _.findWhere(applePayMethod.settings, {
                            name: 'Rs.ApplePay.AppleMerchantId',
                        });

                        if (merchantIdSeting && merchantIdSeting.value) {
                            const merchantIdentifier = merchantIdSeting.value;
                            return $window.ApplePaySession.canMakePaymentsWithActiveCard(merchantIdentifier);
                        }
                    }
                }

                return $q.resolve(false);
            }

            $scope.selectShippingMethod = function (shippingMethod) {
                if (shippingMethod) {
                    $scope.checkout.shipment.shipmentMethodCode = shippingMethod.shipmentMethodCode;
                    $scope.checkout.shipment.shipmentMethodOption = shippingMethod.optionName;
                } else {
                    $scope.checkout.shipment.shipmentMethodCode = undefined;
                    $scope.checkout.shipment.shipmentMethodOption = undefined;
                }

                if (validateComponents()) {
                    $scope.updateShipment($scope.checkout.shipment);
                }
            };

            $scope.updateShipment = function (shipment, calculateTax = false) {
                $scope.updatingShipmentAddr = true;

                shipment.deliveryAddress = $scope.checkout.selectedShipmentAddress;

                if (shipment.deliveryAddress) {
                    shipment.deliveryAddress.type = 'Shipping';
                    shipment.deliveryAddress.email = $scope.checkout.email;
                }

                // Does not pass validation errors to API
                shipment.validationErrors = undefined;

                $scope.checkout.invalidDeliveryAddress = false;

                return wrapLoading(function () {
                    return cartService.addOrUpdateShipment(shipment, calculateTax)
                        .then(function (response) {
                            if (response.data.success) {
                                return $scope.reloadCart();
                            }

                            $scope.checkout.invalidDeliveryAddress = true;

                            if (response.data.matches && response.data.matches.length) {
                                showAddressMatchesDialog(
                                    response.data.matches,
                                    $scope.checkout.selectedShipmentAddress,
                                )
                                    .then(function (result) {
                                        if (result) {
                                            $scope.checkout.invalidDeliveryAddress = false;
                                        } else {
                                            focusService.focusRegion(addressErrorIds.shipping);
                                        }
                                    }, function () {
                                        // $uibModal rejects its result promise on
                                        // dismiss, so "Continue editing" and the
                                        // close button land here, not above.
                                        //
                                        // This focus lands and is then TAKEN AWAY by
                                        // ui-bootstrap 1.1.2, which re-runs its
                                        // open-path "focus the modal window" block
                                        // ~700ms after dismissal
                                        // (ui-bootstrap-tpls.min.js:8:19350) and
                                        // strands focus on <body> once the modal is
                                        // removed. That happens with or without this
                                        // call — the theme makes no focus call on this
                                        // path otherwise — so it is the dialog's own
                                        // defect, not this one's. Kept because it is
                                        // the correct behaviour and becomes effective
                                        // the moment the dialog is fixed or replaced.
                                        focusService.focusRegion(addressErrorIds.shipping);
                                    });
                            } else {
                                focusService.focusRegion(addressErrorIds.shipping);
                            }

                            return $q.reject('invalid shipping address');
                        })
                        .finally(function () {
                            $scope.updatingShipmentAddr = false;
                        });
                }, 'checkout.busy.shipping_address');
            };

            $scope.createOrder = function () {
                if ($scope.browseOnlyMode || $scope.checkout.invalidBillingAddress || !validateComponents()) {
                    return;
                }

                cartService.updateCardComment();
                $scope.creatingOrder = true;

                googleAnalyticsService.addPaymentInfo();

                updatePayment($scope.checkout.payment)
                    .then(
                        function (result) {
                            if (result) {
                                $scope.checkout.loading = true;
                                return wrapLoading(function () {
                                    switch ($scope.checkout.paymentMethod.code) {
                                        case 'ApplePay':
                                            $scope.showApplePayButton = !$scope.browseOnlyMode;
                                            return $q.resolve(null);
                                        case 'Authorize.NetCreditCardBank':
                                            // eslint-disable-next-line no-case-declarations
                                            return handleAuthorizeNetCreditCardPaym()
                                                // eslint-disable-next-line consistent-return
                                                .then(function (resultProcessOrder) {
                                                    const paymentStatus = resultProcessOrder.orderProcessingResult;
                                                    if (paymentStatus.newPaymentStatus === 'New') {
                                                        return handlePostPaymentResult(
                                                            resultProcessOrder.order,
                                                        );
                                                    }
                                                    return resultProcessOrder;
                                                });
                                        case 'PayPalviaAuthorizeNet':
                                        case 'PayPalCommerce':
                                            return handlePayPalPaym();
                                        default:
                                            return cartService.createOrder($scope.checkout.paymentMethod.card || [])
                                                .then(function (response) {
                                                    const {
                                                        order,
                                                        orderProcessingResult,
                                                        paymentMethod,
                                                    } = response.data;

                                                    handlePostPaymentResult(
                                                        order,
                                                        orderProcessingResult,
                                                        paymentMethod,
                                                    );
                                                });
                                    }
                                }, 'checkout.busy.order');
                            }

                            return $q.resolve(false);
                        },
                    )
                    .catch(() => {
                        // $window.location = '/cart';
                    })
                    .finally(() => {
                        $scope.creatingOrder = false;
                        scroll(0, 0);
                    });
            };

            $scope.savePaymentPlan = function () {
                wrapLoading(function () {
                    return cartService.addOrUpdatePaymentPlan($scope.checkout.paymentPlan)
                        .then(function () {
                            $scope.cart.paymentPlan = $scope.checkout.paymentPlan;
                        });
                }, 'checkout.busy.cart');
            };

            $scope.isRecurringChanged = function (isRecurring) {
                if ($scope.checkout.paymentPlan) {
                    if (isRecurring) {
                        $scope.savePaymentPlan();
                    } else {
                        wrapLoading(function () {
                            return cartService.removePaymentPlan()
                                .then(function () {
                                    $scope.cart.paymentPlan = undefined;
                                });
                        }, 'checkout.busy.cart');
                    }
                }
            };

            let $cancelReloadCart = null;

            // messageKey is a parameter so initialize() can reload SILENTLY: an
            // unnamed operation neither announces nor blocks (busyService.begin).
            // Every other caller is nested inside another wrapLoading and its key
            // is ignored anyway -- only the outermost operation owns the message.
            $scope.reloadCart = function (messageKey = 'checkout.busy.cart') {
                if ($cancelReloadCart) {
                    $cancelReloadCart.resolve(); // canceling a request
                }

                $cancelReloadCart = $q.defer();

                return wrapLoading(function () {
                    return cartService.getCart({ timeout: $cancelReloadCart.promise })
                        .then(function (response) {
                            $cancelReloadCart.resolve();
                            $cancelReloadCart = null;

                            return reloadCart(response);
                        });
                }, messageKey);
            };

            function reloadCart({ data: cart }) {
                $scope.cart = cart;
                $scope.checkout.coupons = cart.coupons || $scope.checkout.coupons;

                if (cart.payments.length) {
                    $scope.checkout.payment = cart.payments[0];
                    $scope.checkout.paymentMethod.code = $scope.checkout.payment.paymentGatewayCode;
                }

                // try to get email from user profile
                if (!$scope.checkout.email && $scope.cart.customer.email) {
                    $scope.checkout.email = $scope.cart.customer.email;
                }

                // get addresses from customer profile to $scope.checkout.addressList
                if (!$scope.checkout.addressList) {
                    $scope.checkout.addressList = createAddressList();
                }

                if (cart.shipments.length) {
                    $scope.checkout.shipment = cart.shipments[0];
                }

                const deliveryAddressFromCart = $scope.checkout.shipment.deliveryAddress;

                if (deliveryAddressFromCart) {
                    // add to address list addresses from checkout
                    if (!_.find($scope.checkout.addressList, function (adr) {
                        return rsAddressCompareService.compare(adr, deliveryAddressFromCart);
                    })) {
                        $scope.checkout.addressList.push(fillAddressAsNew(deliveryAddressFromCart));
                    }

                    // try to get email from address
                    if (!$scope.checkout.email) {
                        $scope.checkout.email = deliveryAddressFromCart.email;
                    }
                }

                // add new address to list if it doesn't exist
                if (!_.find($scope.checkout.addressList, function (adr) {
                    return adr.isNew;
                })) {
                    $scope.checkout.addressList.push(fillAddressAsNew({}));
                }

                // finally set selected delivery address
                if (!$scope.updatingShipmentAddr) {
                    $scope.checkout.selectedShipmentAddress = $scope.checkout.addressList[0];

                    if (deliveryAddressFromCart) {
                        $scope.checkout.selectedShipmentAddress = _.find(
                            $scope.checkout.addressList,
                            function (adr) {
                                return rsAddressCompareService.compare(adr, deliveryAddressFromCart);
                            },
                        );
                    }
                }

                // billing addresses
                if (!$scope.checkout.billingAddressList) {
                    $scope.checkout.billingAddressList = createAddressList();
                }

                const billingAddressFromCart = $scope.checkout.payment.billingAddress;

                // add to address list addresses from checkout
                if (billingAddressFromCart && !_.find($scope.checkout.billingAddressList, function (adr) {
                    return rsAddressCompareService.compare(adr, billingAddressFromCart);
                })) {
                    $scope.checkout.billingAddressList.push(fillAddressAsNew(billingAddressFromCart));
                }

                // add new address to list if it doesn't exist
                if (!_.find($scope.checkout.billingAddressList, (adr) => adr.isNew)) {
                    $scope.checkout.billingAddressList.push(fillAddressAsNew({}));
                }

                // finally set selected billing address
                if (billingAddressFromCart) {
                    $scope.checkout.selectedBillingAddress = _.find(
                        $scope.checkout.billingAddressList,
                        (adr) => rsAddressCompareService.compare(adr, billingAddressFromCart),
                    );
                } else {
                    $scope.checkout.selectedBillingAddress = $scope.checkout.billingAddressList[0];
                }

                $scope.checkout.paymentPlan = (
                    cart.paymentPlan && _.findWhere($scope.checkout.availablePaymentPlans, {
                        intervalCount: cart.paymentPlan.intervalCount,
                        interval: cart.paymentPlan.interval,
                    })
                ) || _.findWhere($scope.checkout.availablePaymentPlans, {
                    intervalCount: 1,
                    interval: 'months',
                });

                return cart;
            }

            function showAddressMatchesDialog(matches, targetObject) {
                return dialogService.showDialog(
                    {
                        matches,
                        countries: $scope.checkout.availCountries,
                    },
                    'addressMatchesDialogController',
                    'storefront.address-matches-dialog.tpl',
                    function (result) {
                        if (result) {
                            Object.assign(targetObject, result);
                        }
                    },
                );
            }

            function updatePayment(payment) {
                if (!$scope.checkout.paymentMethod.code) {
                    return $q.resolve(false);
                }

                $scope.updatingBillingAddr = true;

                setPaymentAmount($scope.checkout.paymentMethod);

                payment.billingAddress = $scope.checkout.billingAddressEqualsShipping
                    ? $scope.checkout.selectedShipmentAddress
                    : $scope.checkout.selectedBillingAddress;

                payment.billingAddress.type = 'Billing';

                $scope.checkout.invalidBillingAddress = false;

                return wrapLoading(function () {
                    return cartService.addOrUpdatePayment(payment)
                        .then(function (response) {
                            if (response.data.success) {
                                return $scope.reloadCart();
                            }

                            $scope.checkout.invalidBillingAddress = true;

                            if (response.data.matches && response.data.matches.length) {
                                showAddressMatchesDialog(response.data.matches, $scope.checkout.selectedBillingAddress)
                                    .then(function (res) {
                                        if (res) {
                                            $scope.checkout.invalidBillingAddress = false;
                                        } else {
                                            focusService.focusRegion(addressErrorIds.billing);
                                        }
                                    }, function () {
                                        focusService.focusRegion(addressErrorIds.billing);
                                    });
                            } else {
                                focusService.focusRegion(addressErrorIds.billing);
                            }

                            return $q.resolve(false);
                        })
                        .finally(function () {
                            $scope.updatingBillingAddr = false;
                        });
                }, 'checkout.busy.billing_address');
            }

            function handlePostPaymentResult(order) {
                // if (!orderProcessingResult.isSuccess) {
                //     $scope.checkout.loading = false;
                //     $rootScope.$broadcast('storefrontError', {
                //         type: 'error',
                //         title: [
                //             'Error in new order processing: ',
                //             orderProcessingResult.error,
                //             'New Payment status: ' + orderProcessingResult.newPaymentStatus,
                //         ].join(' '),
                //         message: orderProcessingResult.error,
                //     });
                //     return;
                // }
                $scope.outerRedirect($scope.baseUrl + 'cart/thank-you/' + order.number);
                // if (
                //     paymentMethod.paymentMethodType
                //     && paymentMethod.paymentMethodType.toLowerCase() === 'preparedform'
                //     && orderProcessingResult.htmlForm
                // ) {
                //     $scope.outerRedirect($scope.baseUrl + 'cart/checkout/paymentform?orderNumber=' + order.number);
                // } else if (
                //     paymentMethod.paymentMethodType
                //     && paymentMethod.paymentMethodType.toLowerCase() === 'redirection'
                //     && orderProcessingResult.redirectUrl
                // ) {
                //     $window.location.href = orderProcessingResult.redirectUrl;
                // } else {

                // }
            }

            function handleAuthorizeNetCreditCardPaym() {
                return cartService.createOrder($scope.checkout.paymentMethod.card || [])
                    .then(function (result) {
                        const getHostedPaymFormResult = result.data.orderProcessingResult;

                        if (getHostedPaymFormResult.newPaymentStatus === 'New') {
                            return result.data;
                        }

                        if (!getHostedPaymFormResult.isSuccess) {
                            $rootScope.$broadcast('storefrontError', {
                                type: 'error',
                                title: 'Error in getting hosted paym form: ' + getHostedPaymFormResult.error,
                                message: getHostedPaymFormResult.error,
                            });

                            return $q.reject(getHostedPaymFormResult.error);
                        }

                        $scope.checkout.paymFormHtmlCode = $sce.trustAsHtml(getHostedPaymFormResult.htmlForm);

                        return result;
                    });
            }

            function handlePayPalPaym() {
                $scope.creatingPayPalOrder = true;

                return cartService.createOrder($scope.checkout.paymentMethod.card || [])
                    .then(function (result) {
                        const getPaymFormResult = result.data.orderProcessingResult;

                        if (!getPaymFormResult.isSuccess) {
                            $rootScope.$broadcast('storefrontError', {
                                type: 'error',
                                title: 'Error in getting PaypalLink: ' + getPaymFormResult.error,
                                message: getPaymFormResult.error,
                            });

                            $scope.creatingPayPalOrder = false;

                            return $q.reject(getPaymFormResult.error);
                        }

                        $scope.outerRedirect(getPaymFormResult.redirectUrl);

                        return result;
                    });
            }

            const wrapLoading = (function () {
                const promisesQueue = [];

                // messageKey names the wait for the live region in
                // checkout_layout.liquid (WCAG 4.1.3). busyService runs ALONGSIDE
                // checkout.loading rather than replacing it: the overlay's timing
                // is deliberately unchanged by this, so the only thing that can
                // regress here is what is said, never whether the loader shows.
                return function (func, messageKey) {
                    promisesQueue.push(func);

                    $scope.checkout.loading = true;
                    busyService.begin(messageKey);

                    return func()
                        .finally(function () {
                            const funcIndexInQueue = promisesQueue.indexOf(func);

                            if (funcIndexInQueue > -1) {
                                promisesQueue.splice(funcIndexInQueue, 1);
                            }

                            busyService.end();

                            if (!promisesQueue.length) {
                                $scope.checkout.loading = false;
                            }
                        });
                };
            }());

            $scope.initialize = function () {
                // Silent: arriving at /cart is not an operation the customer
                // started, so it must not announce or hold the page inert.
                $scope.reloadCart(null);

                getCartAssociatedProducts();

                if (['shipping', 'payment'].includes(RS_CHECKOUT_STEP)) {
                    $scope.currentStep = RS_CHECKOUT_STEP;
                    $scope.confirmedStep = RS_CHECKOUT_STEP;
                }

                cartService.getCountries()
                    .then(function (response) {
                        $scope.checkout.availCountries = response.data;
                    });

                return getCartAdditionalProducts();
            };

            // cartController legacy
            let timer;

            $scope.changeLineItemQuantity = function (lineItemId, quantity) {
                const lineItem = _.find($scope.cart.items, function (i) {
                    return i.id === lineItemId;
                });

                if (!lineItem || quantity < 1 || $scope.checkout.loading || $scope.formCart.$invalid) {
                    return;
                }

                const initialQuantity = angular.copy(lineItem.quantity);

                lineItem.quantity = quantity;

                $timeout.cancel(timer);
                timer = $timeout(function () {
                    return wrapLoading(function () {
                        return cartService.changeLineItemQuantity(lineItemId, quantity)
                            .then(function () {
                                $scope.reloadCart();
                                $rootScope.$broadcast('cartItemsChanged');
                            }, function () {
                                lineItem.quantity = initialQuantity;
                            });
                    }, 'checkout.busy.cart');
                }, 300);
            };

            $scope.changeQuantity = function (e) {
                if (!(e.charCode > 47 && e.charCode < 58)) {
                    e.preventDefault();
                    return;
                }

                const value = e.target.value + e.key;

                if (value > 999 || value < 0) {
                    e.preventDefault();
                }
            };

            $scope.removeLineItem = function (lineItemId) {
                const lineItem = _.find($scope.cart.items, (i) => i.id === lineItemId);

                if (!lineItem || $scope.checkout.loading) {
                    return $q.reject('error removing line item');
                }

                const initialItems = angular.copy($scope.cart.items);

                $scope.recentCartItemModalVisible = false;
                $scope.cart.items = _.without($scope.cart.items, lineItem);

                return wrapLoading(function () {
                    return cartService.removeLineItem(lineItemId)
                        .then(function () {
                            $scope.reloadCart();
                            $rootScope.$broadcast('cartItemsChanged');
                            googleAnalyticsService.removeProductFromCart(lineItem);
                        }, function () {
                            $scope.cart.items = initialItems;
                        });
                }, 'checkout.busy.remove_item');
            };

            $scope.setCartForm = function (form) {
                $scope.formCart = form;
            };

            $scope.submitCart = function () {
                if ($scope.browseOnlyMode) {
                    return false;
                }

                $scope.formCart.$setSubmitted();

                if ($scope.formCart.$invalid || !$scope.cart.isValid) {
                    return false;
                }

                $scope.currentStep = 'shipping';
                $scope.confirmedStep = 'shipping';

                scroll(0, 0);

                googleAnalyticsService.beginCheckout($scope.cart.items, $scope.cart.coupons[0]);

                return true;
            };

            function getCartAdditionalProducts() {
                return catalogService.searchAdditionalCartProducts()
                    .then(function (response) {
                        $scope.additionalCartProducts = response.data;
                        $scope.additionalCartProducts.forEach(function (item) {
                            item.quantity = 1;
                        });
                    });
            }

            function getCartAssociatedProducts() {
                $scope.associations = [];

                return cartService.getAssociatedProducts()
                    .then(
                        function (response) {
                            $scope.associations = _.map(response.data, (e) => e.product);
                            const productIds = _.map(response.data, (e) => e.productId);

                            if (productIds.length) {
                                productListService.getProductListData(productIds)
                                    .then(function (result) {
                                        $scope.associationsAddData = result;
                                    });
                            }
                        },
                    );
            }

            $scope.addProductToCart = function (product, quantity) {
                const dialogData = toDialogDataModel(product, quantity);

                dialogService.showDialog(
                    dialogData,
                    'recentlyAddedCartItemDialogController',
                    'storefront.recently-added-cart-item-dialog.tpl',
                );

                cartService.addLineItem(product.id, quantity)
                    .then(function () {
                        $rootScope.$broadcast('cartItemsChanged');
                        $scope.reloadCart();
                    });
            };

            function toDialogDataModel(product, quantity) {
                return {
                    imageUrl: product.primaryImage ? product.primaryImage.url : null,
                    listPrice: product.price.listPrice,
                    id: product.id,
                    listPriceWithTax: product.price.listPriceWithTax,
                    name: product.name,
                    placedPrice: product.price.actualPrice,
                    placedPriceWithTax: product.price.actualPriceWithTax,
                    quantity,
                    updated: false,
                    showGoToCartButton: false,
                };
            }

            function fillAddressAsNew(res) {
                res.displayName = $translate.instant('checkout.address.new_adr');
                res.isNew = true;

                if ($scope.cart) {
                    res.firstName = $scope.cart.customer.firstName;
                    res.lastName = $scope.cart.customer.lastName;
                }

                return res;
            }

            function createAddressList() {
                const addressesProfile = $scope.cart.customer.addresses;
                let res = [];

                if (addressesProfile) {
                    addressesProfile.forEach(function (adr) {
                        const hasName = adr.firstName || adr.lastName;
                        const hasFirstAndLast = adr.firstName && adr.lastName;

                        adr.displayName = (adr.firstName || '')
                            + (hasFirstAndLast ? ' ' : '')
                            + (adr.lastName || '')
                            + (hasName ? '; ' : '')
                            + adr.line1
                            + (adr.line2 ? ', ' + adr.line2 : '')
                            + '; ' + adr.city
                            + '; ' + adr.regionName
                            + '; ' + adr.postalCode
                            + '; ' + adr.countryName;

                        adr.isNew = false;
                    });
                    res = res.concat(addressesProfile);
                }

                return res;
            }

            $scope.handleApplePayMethod = function () {
                if ($scope.browseOnlyMode) {
                    return;
                }

                const { cart } = $scope;
                const applePayMethod = $scope.checkout.paymentMethod;
                const merchantCountryCodeSetting = _.findWhere(applePayMethod.settings, {
                    name: 'Rs.ApplePay.MerchantCountryCode',
                });

                /* * /
                if (!merchantCountryCodeSetting) {
                    console.error('setting merchantCountryCode is not found in ApplePay method');
                }

                if (merchantCountryCodeSetting && !merchantCountryCodeSetting.value) {
                    console.error('merchantCountryCode setting is empty in ApplePay method');
                }
                /* */

                const request = {
                    countryCode: merchantCountryCodeSetting.value,
                    currencyCode: cart.currency.code,
                    supportedNetworks: [
                        'visa',
                        'masterCard',
                        'amex',
                        'discover',
                    ],
                    merchantCapabilities: [
                        // Make sure NOT to include supportsEMV here
                        'supports3DS',
                        'supportsCredit',
                        'supportsDebit',
                    ],
                    total: {
                        label: 'Rainbow Sandals',
                        amount: cart.total.amount,
                    },
                };

                const applePaySession = new ApplePaySession(3, request);

                // Merchant Validation
                applePaySession.onvalidatemerchant = function (event) {
                    cartService.performApplePayMerchantValidation({
                        validationUrl: event.validationURL,
                    })
                        .then((response) => {
                            const merchantSession = response.data;
                            applePaySession.completeMerchantValidation(merchantSession);
                        })
                        .catch(() => {
                            applePaySession.abort();
                        });
                };

                applePaySession.onpaymentauthorized = function (event) {
                    createTransaction(event.payment.token.paymentData)
                        .then(function (response) {
                            const { data: { success, orderNumber } } = response;
                            const status = success
                                ? ApplePaySession.STATUS_SUCCESS
                                : ApplePaySession.STATUS_FAILURE;

                            applePaySession.completePayment(status);

                            if (success) {
                                $scope.outerRedirect($scope.baseUrl + 'cart/thank-you/' + orderNumber);
                            } else {
                                $scope.outerRedirect($scope.baseUrl + 'cart');
                            }
                        });
                };

                function createTransaction(dataObj) {
                    const objJsonStr = JSON.stringify(dataObj);
                    const objJsonB64 = $window.btoa(objJsonStr);

                    return cartService.createTransactionFromToken({
                        dataDesc: 'COMMON.APPLE.INAPP.PAYMENT',
                        dataValue: objJsonB64,
                        paymMethodCode: $scope.checkout.paymentMethod.code,
                        timeZone: (new Date().getTimezoneOffset() / 60) * -1,
                    });
                }

                applePaySession.oncancel = function () {
                    $scope.outerRedirect($scope.baseUrl + 'cart');
                };

                applePaySession.begin();
            };

            let lastShipmentAddr = null;
            let lastBillingAddr = null;

            $scope.raiseSelectedShipmentAddressChanged = _.debounce(function () {
                if (
                    !$scope.checkout.selectedShipmentAddress
                    || !rsAddressCompareService.validate($scope.checkout.selectedShipmentAddress)
                    || rsAddressCompareService.compare(lastShipmentAddr, $scope.checkout.selectedShipmentAddress)
                ) {
                    return;
                }

                lastShipmentAddr = angular.copy($scope.checkout.selectedShipmentAddress);

                $scope.updateShipment($scope.checkout.shipment)
                    .then(function () {
                        $scope.$broadcast('selectedShipmentAddressChanged', { isValid: true });
                    }, function () {
                        $scope.$broadcast('selectedShipmentAddressChanged', { isValid: false });
                    });
            }, 800);

            $scope.raiseSelectedBillingAddressChanged = _.debounce(function () {
                if (
                    !$scope.checkout.selectedBillingAddress
                    || !rsAddressCompareService.validate($scope.checkout.selectedBillingAddress)
                    || rsAddressCompareService.compare(lastBillingAddr, $scope.checkout.selectedBillingAddress)
                ) {
                    return;
                }

                lastBillingAddr = angular.copy($scope.checkout.selectedBillingAddress);

                updatePayment($scope.checkout.payment);
            }, 800);

            $scope.isValidShipmentAddress = function () {
                return rsAddressCompareService.validate($scope.checkout.selectedShipmentAddress)
                    && $scope.checkout.selectedShipmentAddress.phone
                    && !$scope.checkout.invalidDeliveryAddress;
            };

            $scope.isValidBillingAddress = function () {
                return $scope.checkout.billingAddressEqualsShipping
                    || (rsAddressCompareService.validate($scope.checkout.selectedBillingAddress)
                        && $scope.checkout.selectedBillingAddress.phone
                        && (!RS_BILLING_EMAIL_REQUIRED || $scope.checkout.selectedBillingAddress.email)
                        && !$scope.checkout.invalidBillingAddress);
            };
        },
    ]);
